import React from 'react';
import { styled } from '@linaria/react';
import { animated } from 'react-spring';

import InlineCode from '@/components/InlineCode';

interface Props {
  providedAnswer: string;
  correctAnswer: string;
}

function Evaluation({ providedAnswer, correctAnswer }: Props) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (wrapperRef.current?.scrollIntoView) {
      wrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, []);

  /*
    In some contexts, we want to show them their
    answer, and compare it to the correct one.

    In other contexts, it's sufficient to just show
    the correct one.

    In yet still other contexts, we don't need to show
    any answers at all (the answer isn't easily turned
    into a string, and is made clear by the game itself)
    */
  let shownAnswer = (
    <>
      You answered <InlineCode>{providedAnswer}</InlineCode>, but the
      correct answer is <InlineCode>{correctAnswer}</InlineCode>.
    </>
  );

  return (
    <OuterWrapper ref={wrapperRef}>
      <Wrapper>
        <div>
          <p>
            <strong
              style={{ fontWeight: 'var(--font-weight-medium)' }}
            >
              Sorry, that's incorrect.
            </strong>
            <br />
            {shownAnswer}
          </p>
        </div>
      </Wrapper>
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  margin-top: 32px;
  /* Clip Wrapper, which animates in */
  overflow: hidden;
  overflow: clip;
`;

const Wrapper = styled(animated.div)`
  padding: 16px 24px 24px;
  border-radius: 6px;
  background-color: var(--color-error-background);
  animation: slideIn 1200ms cubic-bezier(0, 0.72, 0.12, 1);
  will-change: transform;

  @keyframes slideIn {
    from {
      transform: translateY(-80%);
      opacity: 0;
    }
  }

  p {
    font-size: calc(19 / 16 * 1rem);
    margin-bottom: 0.5em;
    text-align: center;
  }

  p:last-of-type {
    margin-bottom: 0;
  }
`;

export default Evaluation;
